import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Mock credentials for different user roles
  const mockCredentials = {
    'admin@admin.com': { password: '123123', role: 'partner', name: 'Sarah Johnson' },
    'staff@accountingpro.com': { password: 'Staff123!', role: 'staff', name: 'Michael Chen' },
    'freelancer@accountingpro.com': { password: 'Freelancer123!', role: 'freelancer', name: 'Emily Rodriguez' },
    'client@accountingpro.com': { password: 'Client123!', role: 'client', name: 'David Thompson' }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) return;

  setIsLoading(true);

  try {
    // Send the login request to the backend
    const response = await fetch(import.meta.env.VITE_APP_ADMINURL+'/api/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: formData.email,
        password: formData.password,
      }),
    });

    // Check if the response is OK (status 200-299)
    if (!response.ok) {
      const data = await response.json();
      // Handle backend errors (e.g., invalid credentials)
      setErrors({
        general: data.message || 'Invalid email or password. Please check your credentials and try again.',
      });
      setIsLoading(false);
      return;
    }

    // Parse JSON response from the backend
    const data = await response.json();

    // Save the JWT token and user data to localStorage if login is successful
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));

    setIsLoading(false);
    navigate('/dashboard'); // Redirect to dashboard or home page
  } catch (error) {
    // Handle network errors or unexpected issues
    setErrors({
      general: 'Something went wrong, please try again later.',
    });
    setIsLoading(false);
  }
};



  const handleForgotPassword = () => {
    // console.log('Navigate to forgot password');
  };

  const demoCredentials = [
    { role: 'Partner', email: 'partner@accountingpro.com', password: 'Partner123!' },
    { role: 'Staff', email: 'staff@accountingpro.com', password: 'Staff123!' },
    { role: 'Freelancer', email: 'freelancer@accountingpro.com', password: 'Freelancer123!' },
    { role: 'Client', email: 'client@accountingpro.com', password: 'Client123!' }
  ];

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
  
    if (token && user) {
      navigate('/dashboard'); // redirect if already logged in
    }
  }, []);
  

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          

          {/* Login Form */}
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="bg-error-100 border border-error-500 text-error-700 px-4 py-3 rounded-lg flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} color="var(--color-error)" />
                <span className="text-sm">{errors.general}</span>
              </div>
            )}

            <div className="space-y-4">
              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Mail" size={16} color="var(--color-text-secondary)" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-transparent nav-transition ${
                      errors.email ? 'border-error-500 bg-error-50' : 'border-border bg-surface'
                    }`}
                    placeholder="Enter your email address"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-error-700 flex items-center space-x-1">
                    <Icon name="AlertCircle" size={12} color="var(--color-error)" />
                    <span>{errors.email}</span>
                  </p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Icon name="Lock" size={16} color="var(--color-text-secondary)" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-lg placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-border-focus focus:border-transparent nav-transition ${
                      errors.password ? 'border-error-500 bg-error-50' : 'border-border bg-surface'
                    }`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <Icon 
                      name={showPassword ? 'EyeOff' : 'Eye'} 
                      size={16} 
                      color="var(--color-text-secondary)" 
                    />
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-error-700 flex items-center space-x-1">
                    <Icon name="AlertCircle" size={12} color="var(--color-error)" />
                    <span>{errors.password}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Remember Me and Forgot Password */}
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="rememberMe"
                  name="rememberMe"
                  type="checkbox"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary focus:ring-border-focus border-border rounded"
                />
                <label htmlFor="rememberMe" className="ml-2 block text-sm text-text-primary">
                  Remember me
                </label>
              </div>
              {/* <button
                type="button"
                onClick={handleForgotPassword}
                className="text-sm text-secondary hover:text-secondary-700 nav-transition"
              >
                Forgot password?
              </button> */}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-border-focus disabled:opacity-50 disabled:cursor-not-allowed nav-transition"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Icon name="LogIn" size={16} color="white" />
                  <span>Sign in</span>
                </div>
              )}
            </button>
          </form>
        </div>
      </div>

    </div>
  );
};

export default Login;